import {
	BaseControl, RangeControl, Flex, FlexItem,
	__experimentalSpacer as Spacer, __experimentalUseCustomUnits as useCustomUnits,
	__experimentalUnitControl as UnitControl,
	__experimentalParseQuantityAndUnitFromRawValue as parseQuantityAndUnitFromRawValue,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useSettings } from '@wordpress/block-editor';
import { useMemo } from '@wordpress/element';
import { VayuBlocksDevice,VayuBlocksDeviceControl } from '@vayu-blocks/device-control';
import { useState } from '@wordpress/element';

const RANGE_CONTROL_CUSTOM_SETTINGS = {
	px: { max: 1000, step: 1 },
	'%': { max: 100, step: 1 },
	vw: { max: 100, step: 1 },
	vh: { max: 100, step: 1 },
	em: { max: 50, step: 0.1 },
	rem: { max: 50, step: 0.1 },
	svw: { max: 100, step: 1 },
	lvw: { max: 100, step: 1 },
	dvw: { max: 100, step: 1 },
	svh: { max: 100, step: 1 },
	lvh: { max: 100, step: 1 },
	dvh: { max: 100, step: 1 },
	vi: { max: 100, step: 1 },
	svi: { max: 100, step: 1 },
	lvi: { max: 100, step: 1 },
	dvi: { max: 100, step: 1 },
	vb: { max: 100, step: 1 },
	svb: { max: 100, step: 1 },
	lvb: { max: 100, step: 1 },
	dvb: { max: 100, step: 1 },
	vmin: { max: 100, step: 1 },
	svmin: { max: 100, step: 1 },
	lvmin: { max: 100, step: 1 },
	dvmin: { max: 100, step: 1 },
	vmax: { max: 100, step: 1 },
	svmax: { max: 100, step: 1 },
	lvmax: { max: 100, step: 1 },
	dvmax: { max: 100, step: 1 },
};

export const HeightControl = ({
					onChange,
					label = 'label', 
					value , 
					unitMax={} ,
					initialPosition = 0 ,
					allowReset = 'true' ,
					min = 0,
					minimumunit = {},
					responsive=false ,
					defaultValue = '',	
					customUnits = false,
				}) => {

	const device = VayuBlocksDevice();

	let customValue = value || defaultValue;
	let updatedlabel = label;

	if(responsive){
		customValue = value?.[device] || defaultValue;
		updatedlabel = <VayuBlocksDeviceControl label={__(label, 'vayu-blocks')} />;
	}

	const [firstDesktop, setFirstDesktop] = useState(false);
	const [firstTablet, setFirstTablet] = useState(false);

	const handleResponsivechange = (newval) => {
	
		if(responsive){
			const currentValues = value || {};
			const updatedAttributes = {
				...currentValues,
				[device]: newval,
			};

			const isFirstRun = !currentValues?.[device];

			if (device === 'Desktop') {
				if (isFirstRun || firstDesktop) {
					updatedAttributes['Tablet'] = newval;
					updatedAttributes['Mobile'] = newval;
					setFirstTablet(true);
					setFirstDesktop(true);
				}
			} else if (device === 'Tablet') {
				if (firstTablet) {
					updatedAttributes['Mobile'] = newval;
					setFirstTablet(true);
					setFirstDesktop(false);
				}
			} else {
				setFirstTablet(false);
				setFirstDesktop(false);
			}
	
			onChange(updatedAttributes);

		}else{
			onChange(newval)
		}
	
	}

    const customRangeValue = parseFloat( customValue );

	const [ availableUnits ] = useSettings( 'spacing.units' );


	const units = useCustomUnits( {
		availableUnits: customUnits || availableUnits || [
			'%',
			'px',
			'em',
			'rem',
			'vh',
			'vw',
		],
	} );
	const selectedUnit = useMemo( () => parseQuantityAndUnitFromRawValue( customValue ), [ customValue ] )[ 1 ] || units[ 0 ]?.customValue || 'px';

	const handleSliderChange = ( next ) => {
		handleResponsivechange( [ next, selectedUnit ].join( '' ) );
	};

	const handleUnitChange = ( newUnit ) => {
		// Attempt to smooth over differences between currentUnit and newUnit.
		// This should slightly improve the experience of switching between unit types.
		const [ currentValue, currentUnit ] =
			parseQuantityAndUnitFromRawValue( customValue );

		if ( [ 'em', 'rem' ].includes( newUnit ) && currentUnit === 'px' ) {
			// Convert pixel value to an approximate of the new unit, assuming a root size of 16px.
			handleResponsivechange( ( currentValue / 16 ).toFixed( 2 ) + newUnit );
		} else if (
			[ 'em', 'rem' ].includes( currentUnit ) &&
			newUnit === 'px'
		) {
			// Convert to pixel value assuming a root size of 16px.
			handleResponsivechange( Math.round( currentValue * 16 ) + newUnit );
		} else if (
			[
				'%',
				'vw',
				'svw',
				'lvw',
				'dvw',
				'vh',
				'svh',
				'lvh',
				'dvh',
				'vi',
				'svi',
				'lvi',
				'dvi',
				'vb',
				'svb',
				'lvb',
				'dvb',
				'vmin',
				'svmin',
				'lvmin',
				'dvmin',
				'vmax',
				'svmax',
				'lvmax',
				'dvmax',
			].includes( newUnit ) &&
			currentValue > 100
		) {
			// When converting to `%` or viewport-relative units, cap the new value at 100.
			handleResponsivechange( 100 + newUnit );
		}
	};

    const RANGE_CONTROL_Updated_SETTINGS = { ...RANGE_CONTROL_CUSTOM_SETTINGS, ...unitMax };

  return (
    <fieldset className="block-editor-height-control">
			<BaseControl.VisualLabel >
				{ updatedlabel }
			</BaseControl.VisualLabel>
			<Flex >
				<FlexItem isBlock style={{ flex: '0 0 33%' }}>
					<UnitControl
						value={ customValue }
						units={ units }
						__next40pxDefaultSize = {true}
						__nextHasNoMarginBottom = {true}
						onChange={ handleResponsivechange }
						onUnitChange={ handleUnitChange }
						min={
								Object.keys(minimumunit || {}).length > 0
									? minimumunit?.[selectedUnit] ?? 0
									: min
							}
						max={
								RANGE_CONTROL_Updated_SETTINGS[ selectedUnit ]
									?.max ?? 100
							}
						label={ label }
						hideLabelFromVision
					/>
				</FlexItem>
				<FlexItem isBlock style={{ flex: '1' }}>
					<Spacer marginX={ 2 } marginBottom={ 0 }>
						<RangeControl
							__next40pxDefaultSize = {true}
                            __nextHasNoMarginBottom = {true}
                            label={ label }
							hideLabelFromVision
							value={ customRangeValue }
							min={
								Object.keys(minimumunit || {}).length > 0
									? minimumunit?.[selectedUnit] ?? 0
									: min
							}
							max={
								RANGE_CONTROL_Updated_SETTINGS[ selectedUnit ]
									?.max ?? 100
							}
							step={
								RANGE_CONTROL_Updated_SETTINGS[ selectedUnit ]
									?.step ?? 0.1
							}
							withInputField={ false }
							onChange={ handleSliderChange }
                            allowReset = {allowReset}
                            initialPosition={initialPosition}
						/>
					</Spacer>
				</FlexItem>
			</Flex>
		</fieldset>
  )
}

// "height":{
// 	"type":"object",
// 	"default":{
// 		"Desktop":"",
// 		"Tablet":"",
// 		"Mobile":""
// 	}
// }

{/* <HeightControl
	label={__('Height', 'vayu-blocks')}
	value={ attributes.scrollbarheight }
	onChange={ (value) => setAttributes({ scrollbarheight: value }) }
	unitMax={RANGE_CONTROL_CUSTOM_SETTINGS}
	responsive={true}
	defaultValue='5px'
	  customUnits={['px','%']}
/> */}
